/**
 * Settings.
 *
 * Built against taskdojo-app/src/screens/SettingsScreen.js and its
 * ProfileSettingsScreen, folded into one page -- a browser has the room for
 * both, where the phone needs a second screen.
 *
 * Sections follow the phone's order: Account, App settings, Legal, then
 * Account actions. The two destructive ones keep the phone's ordering too:
 * "Clear all my data" sits above "Delete account" because it is the softer of
 * the pair and is what most people mean by "reset".
 *
 * Three things worth stating:
 *
 *  - the timezone is not cosmetic. The Cloud Functions read `timezone` off the
 *    profile to decide when a reminder is due, so this field is the difference
 *    between a 9pm reminder arriving at 9pm and arriving at 9pm UTC. It writes
 *    `timezone` and `timezoneUpdatedAt` exactly as the phone does, the latter
 *    as a Date, which Firestore stores as a Timestamp.
 *  - the theme picker is hidden while everyone is pinned to dark. See
 *    FORCE_DARK in contexts/ThemeContext.tsx; it is one constant to lift.
 *  - notifications are the phone's to manage. A browser cannot receive the
 *    push notifications this app sends, so rather than show a dead toggle the
 *    row says where the setting lives.
 */
import React, { useEffect, useState } from 'react';
import { useApp } from '../../contexts/AppContext';
import { useTheme } from '../../contexts/ThemeContext';
import { auth } from '../../firebaseConfig';
import { updateUserProfile } from '../../services/firebaseService';
import { clearAllUserData, LOCAL_KEYS_TO_CLEAR } from '../../services/resetService';
import {
  ScreenHeader, PrimaryButton, SecondaryButton, Field, useInputClass,
  raised, pressable, SectionTitle,
} from '../dojo';

/** The phone's TIMEZONES, verbatim. */
const TIMEZONES = [
  { label: 'London (GMT/BST)', value: 'Europe/London' },
  { label: 'New York (EST/EDT)', value: 'America/New_York' },
  { label: 'Los Angeles (PST/PDT)', value: 'America/Los_Angeles' },
  { label: 'Paris (CET/CEST)', value: 'Europe/Paris' },
  { label: 'Tokyo (JST)', value: 'Asia/Tokyo' },
  { label: 'Sydney (AEST/AEDT)', value: 'Australia/Sydney' },
  { label: 'Mumbai (IST)', value: 'Asia/Kolkata' },
  { label: 'Dubai (GST)', value: 'Asia/Dubai' },
  { label: 'Toronto (EST/EDT)', value: 'America/Toronto' },
  { label: 'Singapore (SGT)', value: 'Asia/Singapore' },
];

/** The phone's AVATAR_OPTIONS, verbatim. */
const AVATARS = ['😀', '😎', '🤓', '😇', '🤔', '😍', '🐶', '🐱',
                 '🐻', '🐼', '🦊', '🦁', '🌟', '🔥', '💖', '🎯'];

const Profile: React.FC<{ onNavigate?: (screen: 'connectors') => void }> = ({ onNavigate }) => {
  const { state } = useApp();
  const { colors, isDark, themeMode, setThemeMode, themeLocked } = useTheme();
  const input = useInputClass();

  const profile = state.user as any;
  const uid = profile?.uid;

  const [displayName, setDisplayName] = useState(profile?.displayName || '');
  const [avatar, setAvatar] = useState<string | null>(profile?.avatarEmoji || null);
  const [timezone, setTimezone] = useState(
    profile?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/London',
  );
  const [savingProfile, setSavingProfile] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [clearing, setClearing] = useState(false);
  const [clearStep, setClearStep] = useState('');
  const [confirm, setConfirm] = useState<'clear' | 'delete' | null>(null);

  // The profile arrives asynchronously, so the fields start empty and fill in.
  useEffect(() => {
    if (!profile) return;
    setDisplayName(profile.displayName || '');
    setAvatar(profile.avatarEmoji || null);
    if (profile.timezone) setTimezone(profile.timezone);
  }, [profile?.displayName, profile?.avatarEmoji, profile?.timezone]); // eslint-disable-line

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uid) return;
    if (!displayName.trim()) { setError('Your name cannot be empty.'); return; }
    setSavingProfile(true); setError(''); setNotice('');
    try {
      await updateUserProfile(uid, {
        displayName: displayName.trim(),
        avatarEmoji: avatar,
      } as any);
      setNotice('Saved.');
    } catch (err: any) {
      setError(err?.message || "Couldn't save your profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const saveTimezone = async (value: string) => {
    setTimezone(value);
    if (!uid) return;
    try {
      // Field for field with the phone: `timezone`, and `timezoneUpdatedAt` as
      // a Date rather than a string or a serverTimestamp.
      await updateUserProfile(uid, {
        timezone: value,
        timezoneUpdatedAt: new Date(),
      } as any);
      setNotice('Timezone updated.');
    } catch (err: any) {
      setError(err?.message || "Couldn't save your timezone.");
    }
  };

  const clearData = async () => {
    if (!uid) return;
    setConfirm(null);
    setClearing(true); setError(''); setNotice('');
    try {
      const { failures } = await clearAllUserData(uid, (label) => setClearStep(label));
      LOCAL_KEYS_TO_CLEAR.forEach((k) => localStorage.removeItem(k));
      if (failures.length > 0) {
        // Named, not swallowed -- a partial wipe that claims success is how
        // someone ends up thinking their data is gone when it is not.
        setError(`Some things could not be cleared: ${failures.map((f) => f.name).join(', ')}.`);
      } else {
        setNotice('Everything cleared. Your account is empty and your belt is back to White.');
      }
    } catch (err: any) {
      setError(err?.message || "Couldn't clear your data.");
    } finally {
      setClearing(false);
      setClearStep('');
    }
  };

  const row = `w-full rounded-[14px] ${raised} ${pressable} ${colors.cardBorder} ${colors.card}
    px-4 py-3 flex items-center gap-3 text-left`;

  const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <section className="mb-6">
      <SectionTitle className="mb-3">{title}</SectionTitle>
      <div className="space-y-2">{children}</div>
    </section>
  );

  return (
    <div className={`min-h-screen ${colors.bg} ${colors.text}`}>
      <div className="max-w-3xl mx-auto pt-4 pb-24">
        <ScreenHeader title="Settings" subtitle={profile?.email} />

        {(error || notice) && (
          <div
            role={error ? 'alert' : 'status'}
            className={`mx-5 mb-4 rounded-[14px] border-2 px-4 py-3 text-[13px] font-semibold
              ${error
                ? 'border-dojo-red/50 bg-dojo-red/10 text-dojo-red'
                : 'border-dojo-green/50 bg-dojo-green/10 text-dojo-greenDark'}`}
          >
            {error || notice}
          </div>
        )}

        <div className="px-5">
          {/* ------------------------------ Account ------------------------------ */}
          <Section title="Account">
            <form
              onSubmit={saveProfile}
              className={`rounded-[15px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-4`}
            >
              <div className="flex items-center gap-4 mb-5">
                <span
                  className={`w-16 h-16 rounded-[20px] flex items-center justify-center text-[30px] font-black
                    flex-shrink-0 ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-100'}`}
                >
                  {avatar || displayName.charAt(0).toUpperCase() || 'U'}
                </span>
                <div className="min-w-0">
                  <p className="text-[17px] font-extrabold truncate">{displayName || 'You'}</p>
                  <p className={`text-[13.5px] truncate ${colors.textSecondary}`}>{profile?.email}</p>
                </div>
              </div>

              <Field label="Display name" className="mb-4">
                <input
                  className={input}
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                />
              </Field>

              <Field label="Avatar" className="mb-5">
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setAvatar(null)}
                    aria-pressed={avatar === null}
                    title="Use your initial"
                    className={`w-10 h-10 rounded-[12px] border-2 text-[15px] font-black
                      flex items-center justify-center
                      ${avatar === null
                        ? 'border-dojo-red bg-dojo-red/10'
                        : `${colors.cardBorder} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}`}
                  >
                    {displayName.charAt(0).toUpperCase() || 'U'}
                  </button>
                  {AVATARS.map((a) => (
                    <button
                      key={a}
                      type="button"
                      onClick={() => setAvatar(a)}
                      aria-pressed={avatar === a}
                      className={`w-10 h-10 rounded-[12px] border-2 text-[19px]
                        flex items-center justify-center
                        ${avatar === a
                          ? 'border-dojo-red bg-dojo-red/10'
                          : `${colors.cardBorder} ${isDark ? 'bg-dojo-surfaceDeep' : 'bg-stone-50'}`}`}
                    >
                      {a}
                    </button>
                  ))}
                </div>
              </Field>

              <PrimaryButton type="submit" disabled={savingProfile}>
                {savingProfile ? 'Saving…' : 'Save profile'}
              </PrimaryButton>
            </form>
          </Section>

          {/* --------------------------- App settings --------------------------- */}
          <Section title="Integrations">
            <button type="button" className={row} onClick={() => onNavigate?.('connectors')}>
              <span className="text-[17px]">🔗</span>
              <span className="flex-1">
                <span className="block text-[14.5px] font-extrabold">Connectors</span>
                <span className={`block text-[12.5px] ${colors.textSecondary}`}>
                  Let Projectly and other tools work with your tasks
                </span>
              </span>
              <span className={colors.textSecondary}>›</span>
            </button>
          </Section>

          <Section title="App settings">
            {!themeLocked && (
              <div className={`rounded-[15px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-4`}>
                <p className="text-[13px] font-extrabold mb-2">Appearance</p>
                <div className="flex gap-2">
                  {(['light', 'dark', 'system'] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => setThemeMode(mode)}
                      className={`flex-1 rounded-[12px] ${raised} ${pressable} py-2.5 text-[13.5px]
                        font-extrabold capitalize
                        ${themeMode === mode
                          ? 'bg-dojo-red border-dojo-redDeep text-white'
                          : `${colors.cardBorder} ${colors.card} ${colors.text}`}`}
                    >
                      {mode === 'light' ? '☀️ ' : mode === 'dark' ? '🌙 ' : '💻 '}{mode}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className={`block rounded-[15px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-4`}>
              <span className="block text-[13px] font-extrabold mb-1">Timezone</span>
              <span className={`block text-[12.5px] font-semibold mb-2 ${colors.textSecondary}`}>
                Reminders are sent according to this.
              </span>
              <select className={input} value={timezone} onChange={(e) => saveTimezone(e.target.value)}>
                {/* A timezone already on the profile that is not one of the ten
                    is kept as an option, or selecting it here would silently
                    change it. */}
                {!TIMEZONES.some((t) => t.value === timezone) && (
                  <option value={timezone}>{timezone}</option>
                )}
                {TIMEZONES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>

            <div className={`rounded-[15px] border-2 border-b-[3px] ${colors.cardBorder} ${colors.card} p-4`}>
              <p className="text-[13px] font-extrabold mb-1">Notifications</p>
              <p className={`text-[12.5px] font-semibold ${colors.textSecondary}`}>
                Reminders and nudges are delivered by the taskDojo app. Manage them there.
              </p>
            </div>
          </Section>

          {/* ------------------------------- Legal ------------------------------- */}
          <Section title="Legal & privacy">
            {[
              { label: 'Terms of service', href: 'https://splat.sh/taskdojo/terms' },
              { label: 'Privacy policy', href: 'https://splat.sh/taskdojo/privacy' },
            ].map((l) => (
              <a
                key={l.href}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className={row}
              >
                <span className="flex-1 text-[15px] font-bold">{l.label}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  strokeWidth="2.4" className={colors.textMuted}>
                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"
                    strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            ))}
          </Section>

          {/* -------------------------- Account actions -------------------------- */}
          <Section title="Account actions">
            <button
              type="button"
              onClick={() => auth.signOut()}
              className={row}
            >
              <span className="flex-1 text-[15px] font-bold">Sign out</span>
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2.3" className={colors.textMuted}>
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9"
                  strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>

            {/* Start over: keeps the sign-in, removes everything in it. Above
                Delete account because it is the softer of the two and is what
                most people actually want when they say "reset". */}
            <button
              type="button"
              onClick={() => setConfirm('clear')}
              disabled={clearing}
              className={`${row} disabled:opacity-60`}
            >
              <span className="flex-1">
                <span className="block text-[15px] font-bold text-dojo-clay">
                  {clearing ? 'Clearing…' : 'Clear all my data'}
                </span>
                <span className={`block text-[12.5px] font-semibold ${colors.textSecondary}`}>
                  {clearing
                    ? (clearStep ? `Removing ${clearStep}…` : 'Working…')
                    : 'Keeps your account, removes everything in it.'}
                </span>
              </span>
            </button>

            <button
              type="button"
              onClick={() => setConfirm('delete')}
              className={`${row} !border-dojo-red/60`}
            >
              <span className="flex-1">
                <span className="block text-[15px] font-bold text-dojo-red">Delete account</span>
                <span className={`block text-[12.5px] font-semibold ${colors.textSecondary}`}>
                  Permanently removes your account and everything in it.
                </span>
              </span>
            </button>
          </Section>
        </div>
      </div>

      {confirm && (
        <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-5">
          <div className={`w-full max-w-sm ${colors.card} border-2 ${colors.cardBorder} rounded-[22px] p-6`}>
            <h3 className="text-xl font-black tracking-[-0.4px] mb-2">
              {confirm === 'clear' ? 'Clear all your data?' : 'Delete your account?'}
            </h3>
            <p className={`text-[14px] font-semibold mb-5 ${colors.textSecondary}`}>
              {confirm === 'clear'
                ? 'Every task, habit, note and list you own will be removed, and your belt goes back to White. Your account and connections stay. This cannot be undone.'
                : 'This removes your account and everything in it, on every device. This cannot be undone.'}
            </p>
            <div className="flex gap-3">
              <SecondaryButton onClick={() => setConfirm(null)} className="flex-1">Cancel</SecondaryButton>
              <PrimaryButton
                onClick={() => {
                  if (confirm === 'clear') clearData();
                  else {
                    // Deleting an account needs a recent sign-in and cleanup the
                    // client cannot do alone, so it is not faked here.
                    setConfirm(null);
                    setError('Deleting an account has to be done in the taskDojo app, under Settings.');
                  }
                }}
                className="flex-1"
              >
                {confirm === 'clear' ? 'Clear everything' : 'Continue'}
              </PrimaryButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
